
import React from 'react';
import { ExternalLink, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FinancialData } from '@/hooks/useFinancialReporting';

interface FinancialDataCardProps {
  data: FinancialData;
}

const FinancialDataCard: React.FC<FinancialDataCardProps> = ({ data }) => {
  // Function to format the date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };

  // Get the appropriate icon and color based on the impact
  const getImpactDetails = () => {
    switch (data.impact) {
      case 'positive':
        return { 
          icon: <TrendingUp className="w-4 h-4" />, 
          color: 'bg-green-100 text-green-800',
          label: 'Positive Impact'
        };
      case 'negative':
        return { 
          icon: <TrendingDown className="w-4 h-4" />, 
          color: 'bg-red-100 text-red-800',
          label: 'Negative Impact'
        };
      default:
        return { 
          icon: <Minus className="w-4 h-4" />, 
          color: 'bg-gray-100 text-gray-800',
          label: 'Neutral Impact'
        };
    }
  };

  const { icon, color, label } = getImpactDetails();

  return (
    <Card className="mb-4 border border-gray-200 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs text-gray-500">{formatDate(data.date)}</span>
          <Badge variant="outline" className={`${color} flex items-center gap-1 px-2 py-0.5`}>
            {icon}
            <span className="text-xs">{label}</span>
          </Badge>
        </div>
        
        <h3 className="text-lg font-semibold mb-1">{data.title}</h3>
        <p className="text-sm text-gray-600 mb-3">{data.summary}</p>
        
        <div className="flex flex-wrap justify-between items-center">
          <div className="flex items-center text-xs text-gray-500">
            <span className="font-medium">Source:</span>
            <span className="ml-1">{data.source}</span>
          </div>
          
          <a 
            href={data.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            Read More <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        
        {(data.metric && data.value) && (
          <div className="mt-3 p-2 bg-gray-50 rounded-md">
            <div className="flex justify-between">
              <span className="text-xs font-medium text-gray-600">{data.metric}</span>
              <span className="text-xs font-bold">{data.value}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialDataCard;
